import {
    PaymentElement,
    useElements,
    useStripe,
} from "@stripe/react-stripe-js";
import { TagIcon, XIcon } from "lucide-react";
import { useState } from "react";
import billingApi from "@/api/billing";
import { Button } from "@/components/Button/Button";
import Chip from "@/components/Chip/Chip";
import { AppText } from "@/components/Text/AppText";
import { useAppSelector, useAppDispatch } from "@/hooks/types";
import { useStripe as useStripeHook } from "@/hooks/useStripe";
import { getUserDetails } from "@/store/user/selectors";
import { setUserCredits } from "@/store/user/slice";
import { StripeItem } from "@/types/billing";
import { ICreditAction } from "@/types/creditAction";
import {
    CLARITY_EVENT_NAMES,
    GA_EVENT_ACTIONS,
    GA_EVENT_CATEGORIES,
} from "@/types/tracking";
import { formatCentsToPrice, pluralise } from "@/utils/string";
import {
    trackClarityEvent,
    trackGAEvent,
    trackPostHogEvent,
} from "@/utils/tracking";
import type { StripePaymentElementOptions } from "@stripe/stripe-js";

interface CheckoutFormProps {
    onSuccess: () => void;
    onError: (error: string) => void;
    onLoadComplete: () => void;
    selectedAction: ICreditAction;
    selectedProduct: StripeItem;
    amountInCents: number;
    discountAmount: number | null;
    activePromoCode: string | null;
    setActivePromoCode: (promoCode: string) => void;
    paymentIntentId: string;
    setDiscountAmount: (discountAmount: number | null) => void;
}

export const CheckoutForm = ({
    onSuccess,
    onError,
    onLoadComplete,
    selectedAction,
    amountInCents,
    selectedProduct,
    discountAmount,
    activePromoCode,
    setActivePromoCode,
    paymentIntentId,
    setDiscountAmount,
}: CheckoutFormProps) => {
    const stripe = useStripe();
    const elements = useElements();
    const dispatch = useAppDispatch();
    const [isProcessing, setIsProcessing] = useState(false);
    const userDetails = useAppSelector(getUserDetails);
    const { updatePaymentIntent } = useStripeHook();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setIsProcessing(true);

        try {
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                redirect: "if_required",
            });

            if (error) {
                onError(
                    error.message ??
                        "An error occurred processing your payment. Please try again.",
                );
                trackGAEvent(
                    GA_EVENT_CATEGORIES.STRIPE,
                    GA_EVENT_ACTIONS.SUBMIT,
                    "payment-failed",
                );
                trackClarityEvent(CLARITY_EVENT_NAMES.PURCHASE_FAILED);
                trackPostHogEvent(CLARITY_EVENT_NAMES.PURCHASE_FAILED, {
                    error_message: error.message,
                });
                return;
            }

            if (paymentIntent?.status === "succeeded") {
                const metadata = activePromoCode
                    ? {
                          promoCode: activePromoCode ?? "",
                          discount: discountAmount ?? 0,
                          originalAmount: amountInCents,
                      }
                    : undefined;
                const response = await billingApi.processPayment({
                    paymentIntentId: paymentIntent.id,
                    metadata,
                });

                dispatch(setUserCredits(response.userCredits));

                onSuccess();
            }
        } catch (err) {
            onError(
                "An error occurred processing your payment. Please try again.",
            );
        } finally {
            setIsProcessing(false);
        }
    };

    const handleRemovePromoCode = async () => {
        setActivePromoCode("");
        updatePaymentIntent(paymentIntentId!, amountInCents); // Reset the amount to the original amount
        setDiscountAmount(null);
    };

    return (
        <form
            onSubmit={handleSubmit}
            className="mx-auto"
        >
            <PaymentElement
                options={
                    {
                        layout: { type: "tabs" },
                        paymentMethodOrder: ["card"],
                        defaultValues: {
                            billingDetails: {
                                email: userDetails?.email,
                            },
                        },
                    } as StripePaymentElementOptions
                }
                onReady={() => {
                    onLoadComplete();
                }}
            />
            <div className="mt-6 flex flex-col gap-4">
                <div className="flex items-center">
                    <div className="flex w-full flex-col gap-1">
                        {activePromoCode && (
                            <>
                                <div className="flex w-full items-center justify-between">
                                    <AppText
                                        variant="labelsbuttons"
                                        className="text-neutral-700"
                                    >
                                        Subtotal
                                    </AppText>
                                    <AppText
                                        variant="labelsbuttons"
                                        className="text-neutral-700"
                                    >
                                        {formatCentsToPrice(
                                            amountInCents,
                                            selectedProduct.currencySymbol,
                                        )}
                                    </AppText>
                                </div>

                                <div className="flex w-full items-center justify-between">
                                    <AppText
                                        variant="labelsbuttons"
                                        className="text-neutral-700"
                                    >
                                        Discount
                                    </AppText>
                                    <div className="flex items-center gap-4">
                                        <Chip
                                            type="gradient"
                                            onClick={handleRemovePromoCode}
                                            className="!gap-1 !px-2"
                                        >
                                            <TagIcon
                                                size={14}
                                                className="rotate-90 text-primary-900"
                                            />
                                            <AppText
                                                variant="labelsbuttons"
                                                applyGradient
                                            >
                                                {activePromoCode}
                                            </AppText>
                                            <XIcon
                                                size={12}
                                                strokeWidth={3}
                                                className="cursor-pointer text-neutral-700"
                                            />
                                        </Chip>
                                        <AppText
                                            variant="labelsbuttons"
                                            className="text-neutral-700"
                                        >
                                            -
                                            {formatCentsToPrice(
                                                discountAmount ?? 0,
                                                selectedProduct.currencySymbol,
                                            )}
                                        </AppText>
                                    </div>
                                </div>
                            </>
                        )}
                        <div className="flex w-full items-center justify-between">
                            {/* Totals / tax etc */}
                            <AppText
                                variant="contextheading"
                                className="text-neutral-900"
                            >
                                Total
                            </AppText>
                            <div className="flex items-center gap-2">
                                <AppText
                                    variant="contextheading"
                                    className="text-neutral-900"
                                >
                                    {formatCentsToPrice(
                                        amountInCents - (discountAmount ?? 0),
                                        selectedProduct.currencySymbol,
                                    )}
                                </AppText>
                            </div>
                        </div>
                    </div>
                </div>
                <Button
                    type="submit"
                    disabled={isProcessing || !stripe || !elements}
                    loading={isProcessing}
                    className="w-full"
                >
                    {isProcessing
                        ? "Processing..."
                        : `BUY ${selectedAction?.credits} ${pluralise(
                              selectedAction?.credits ?? 0,
                              "CREDIT",
                              "CREDITS",
                          )}`}
                </Button>
                <div className="flex justify-center gap-3 text-xs text-neutral-500">
                    <a
                        href="https://stripe.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-neutral-700"
                    >
                        <AppText variant="footnote">Powered by Stripe</AppText>
                    </a>
                    <a
                        href="https://stripe.com/au/legal/consumer"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-neutral-700"
                    >
                        <AppText variant="footnote">Terms</AppText>
                    </a>
                    <a
                        href="https://stripe.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-neutral-700"
                    >
                        <AppText variant="footnote">Privacy</AppText>
                    </a>
                </div>
            </div>
        </form>
    );
};
